// MobileWildTab.jsx — mobile-only Wild tab mirroring the Flutter circle
// screen: hiring posts in a scrollable feed with upvote/downvote, tags and
// a message-boss CTA; tapping a card pushes a detail view with full body
// and comments.
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import {
  ArrowLeft, ArrowUpRight, ChevronUp, ChevronDown,
  MessageCircle, Share2, Send,
} from "lucide-react"
import {
  API_BASE, classifyPost, normalizeComment, formatAgo,
} from "./WildTab"
import MobileBottomNav from "./MobileBottomNav"

const FF = '"Plus Jakarta Sans", "Inter", -apple-system, sans-serif'

// Flutter mobile palette
const INK          = "#0F172A"
const INK_MUTED    = "#64748B"
const INK_FAINT    = "#94A3B8"
const BG           = "#FAFAF7"
const CARD         = "#FFFFFF"
const SURFACE_ALT  = "#F3F2ED"
const BORDER_SOFT  = "#E6E5DF"
const ORANGE       = "#E8702A"

const TAG_BG = ["#F3D9BC", "#D9E9CC", "#CFE3D3"]
const TAG_FG = ["#7A4A1E", "#3F5F2E", "#25563B"]

const POSTS_CACHE_KEY = "wild_posts_cache_mobile_v1"

const initialsOf = (name = "") =>
  (name || "").trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map(w => w[0]?.toUpperCase() || "").join("") || "?"

// ── Avatar with LinkedIn dot ──────────────────────────────────────────────
function Avatar({ url, name, size = 42 }) {
  const [failed, setFailed] = useState(false)
  const show = !!url && !failed
  return (
    <div style={{ position: "relative", width: size, height: size + 4, flexShrink: 0 }}>
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: `1.5px solid ${BORDER_SOFT}`,
        overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: SURFACE_ALT,
      }}>
        {show ? (
          <img src={url} alt="" referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontSize: size * 0.3, fontWeight: 800, color: INK }}>
            {initialsOf(name)}
          </span>
        )}
      </div>
      <span style={{
        position: "absolute", right: 0, bottom: 2,
        width: 13, height: 13, borderRadius: "50%",
        background: "#0A66C2",
        border: `2px solid ${CARD}`,
      }} />
    </div>
  )
}

// ── Dashed divider ────────────────────────────────────────────────────────
function DashedDivider() {
  return (
    <div style={{
      height: 1, width: "100%",
      backgroundImage: `repeating-linear-gradient(90deg, ${BORDER_SOFT} 0 6px, transparent 6px 12px)`,
    }} />
  )
}

// ── Tag chip ──────────────────────────────────────────────────────────────
function Tag({ label, index }) {
  const bg = TAG_BG[index % TAG_BG.length]
  const fg = TAG_FG[index % TAG_FG.length]
  return (
    <span style={{
      padding: "5px 10px", borderRadius: 20,
      background: bg, color: fg,
      fontSize: 11, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>{label}</span>
  )
}

// ── Hiring card ───────────────────────────────────────────────────────────
function HiringCard({ post, state, expanded, onOpen, onVote, onMessageBoss }) {
  const author = post.author || {}
  const displayName = author.name || "Anonymous"
  const ago = formatAgo(post.createdAt)
  const headline = (expanded ? `#Hiring | ${post.role || ""}` : post.summary) || ""
  const flatTags = useMemo(() => {
    const t = post.tags || {}
    return [t.role, t.seniority, t.remote, ...(t.other || [])].filter(Boolean)
  }, [post.tags])

  return (
    <div style={{
      background: CARD,
      border: `1px solid ${BORDER_SOFT}`,
      borderRadius: 16,
      padding: "14px 16px",
      position: "relative",
      boxShadow: "0 4px 12px rgba(15,23,42,0.04)",
      fontFamily: FF,
    }}
      onClick={onOpen && !expanded ? onOpen : undefined}
    >
      {/* Author row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Avatar url={author.avatar} name={displayName} size={40} />
        <div style={{ flex: 1, minWidth: 0, paddingRight: 20 }}>
          <div style={{
            fontSize: 15, fontWeight: 800, color: INK, letterSpacing: "-0.1px",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{displayName}</div>
          {author.headline && (
            <div style={{
              marginTop: 2, fontSize: 12, color: INK_MUTED, lineHeight: 1.35,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{author.headline}</div>
          )}
          {ago && (
            <div style={{ marginTop: 2, fontSize: 11, color: INK_FAINT }}>{ago}</div>
          )}
        </div>
      </div>

      {/* Open indicator */}
      {!expanded && onOpen && (
        <ArrowUpRight
          size={18} color={INK_MUTED}
          style={{ position: "absolute", top: 12, right: 12 }}
        />
      )}

      {/* Role pill */}
      {post.role && (
        <div style={{ marginTop: 14 }}>
          <span style={{
            display: "inline-block",
            padding: "7px 12px", borderRadius: 24,
            background: INK, color: "#fff",
            fontSize: 12, fontWeight: 700, letterSpacing: 0.1,
          }}>{post.role}</span>
        </div>
      )}

      <div style={{ height: 14 }} />
      <DashedDivider />
      <div style={{ height: 14 }} />

      {/* Headline (summary when collapsed, "#Hiring | role" when expanded) */}
      {headline && (
        <div style={{
          fontSize: 15, fontWeight: 800, color: INK,
          lineHeight: 1.4, letterSpacing: "-0.1px",
          display: expanded ? "block" : "-webkit-box",
          WebkitLineClamp: expanded ? "unset" : 2,
          WebkitBoxOrient: "vertical",
          overflow: expanded ? "visible" : "hidden",
          marginBottom: 8,
        }}>{headline}</div>
      )}

      {/* Body */}
      {post.body && (expanded ? (
        <div style={{
          fontSize: 14, color: INK_MUTED, lineHeight: 1.6,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
          userSelect: "text",
        }}>{post.body}</div>
      ) : (
        <div style={{
          fontSize: 13, color: INK_MUTED, lineHeight: 1.5,
          display: "-webkit-box", WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{post.body}</div>
      ))}

      {/* Tags */}
      {flatTags.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {flatTags.map((t, i) => <Tag key={i} label={t} index={i} />)}
        </div>
      )}

      {/* Actions */}
      <div style={{
        marginTop: 14,
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <button
          onClick={e => { e.stopPropagation(); onVote?.(post, 1) }}
          aria-label="Upvote"
          style={{
            width: 30, height: 36, border: "none", background: "transparent",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: state?.vote === 1 ? ORANGE : INK,
          }}
        >
          <ChevronUp size={22} strokeWidth={2.5} />
        </button>
        <span style={{ fontSize: 13, fontWeight: 700, color: INK, minWidth: 18, textAlign: "center" }}>
          {state?.votes ?? post.votes ?? 0}
        </span>
        <button
          onClick={e => { e.stopPropagation(); onVote?.(post, -1) }}
          aria-label="Downvote"
          style={{
            width: 30, height: 36, border: "none", background: "transparent",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: state?.vote === -1 ? ORANGE : INK,
          }}
        >
          <ChevronDown size={22} strokeWidth={2.5} />
        </button>

        {!expanded && (
          <button
            onClick={e => { e.stopPropagation(); onOpen?.() }}
            aria-label="Comments"
            style={{
              marginLeft: 6,
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 8px",
              border: "none", background: "transparent",
              cursor: "pointer", color: INK,
              fontFamily: FF, fontSize: 13, fontWeight: 600,
            }}
          >
            <MessageCircle size={16} />
            {state?.commentCount ?? post.commentCount ?? 0}
          </button>
        )}

        <button
          onClick={e => { e.stopPropagation(); toast("Sharing coming soon") }}
          aria-label="Share"
          style={{
            width: 32, height: 36, border: "none", background: "transparent",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: INK,
          }}
        >
          <Share2 size={16} />
        </button>

        <span style={{ flex: 1 }} />

        <button
          onClick={e => { e.stopPropagation(); onMessageBoss?.(post) }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "9px 14px",
            border: "none", borderRadius: 999,
            background: ORANGE, color: "#fff",
            cursor: "pointer",
            fontFamily: FF, fontSize: 12.5, fontWeight: 700,
          }}
        >
          Message boss
          <ArrowUpRight size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Card skeleton ─────────────────────────────────────────────────────────
function CardSkeleton() {
  return (
    <div style={{
      background: CARD, border: `1px solid ${BORDER_SOFT}`,
      borderRadius: 16, padding: "14px 16px",
    }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: SURFACE_ALT }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, paddingTop: 4 }}>
          <div style={{ width: "40%", height: 10, borderRadius: 4, background: SURFACE_ALT }} />
          <div style={{ width: "70%", height: 9, borderRadius: 4, background: SURFACE_ALT }} />
        </div>
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 6 }}>
        <div style={{ width: 110, height: 24, borderRadius: 24, background: SURFACE_ALT }} />
      </div>
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ width: "100%", height: 11, borderRadius: 4, background: SURFACE_ALT }} />
        <div style={{ width: "90%", height: 11, borderRadius: 4, background: SURFACE_ALT }} />
        <div style={{ width: "40%", height: 11, borderRadius: 4, background: SURFACE_ALT }} />
      </div>
    </div>
  )
}

// ── Comments ──────────────────────────────────────────────────────────────
function CommentsSection({ postId, userId, state, setState }) {
  const [text, setText] = useState("")
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (state.comments !== null || state.loadingComments) return
    setState(s => ({ ...s, loadingComments: true, commentsError: null }))
    fetch(`${API_BASE}/wild/posts/${postId}/comments`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const body = await r.json()
        const list = (body.comments || []).map(normalizeComment)
        setState(s => ({ ...s, comments: list, commentCount: list.length, loadingComments: false }))
      })
      .catch(err => {
        console.error("Failed to load comments:", err)
        setState(s => ({ ...s, loadingComments: false, commentsError: String(err) }))
      })
  }, [postId])

  const submit = async (e) => {
    e?.preventDefault?.()
    const body = text.trim()
    if (!body || sending) return
    if (!userId) { toast.error("Sign in to comment"); return }
    setSending(true)
    try {
      const res = await fetch(`${API_BASE}/wild/posts/${postId}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, body }),
      })
      if (res.status !== 201) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const c = normalizeComment(data.comment || {})
      setState(s => ({
        ...s, comments: [...(s.comments || []), c],
        commentCount: (s.commentCount || 0) + 1,
      }))
      setText("")
    } catch (err) {
      console.error("Post comment failed:", err)
      toast.error("Couldn't post your comment")
    } finally { setSending(false) }
  }

  const comments = state.comments || []
  const count = state.commentCount || 0

  return (
    <section style={{ marginTop: 20 }}>
      <h3 style={{
        margin: "0 0 14px", fontSize: 18, fontWeight: 800,
        color: INK, letterSpacing: "-0.2px",
        display: "flex", alignItems: "baseline", gap: 8,
      }}>
        Comments
        <span style={{ fontSize: 11, fontWeight: 600, color: INK_FAINT }}>
          {count} anon
        </span>
      </h3>

      {state.loadingComments && comments.length === 0 ? (
        <div style={{ padding: "22px 0", display: "flex", justifyContent: "center" }}>
          <span style={{
            width: 20, height: 20, borderRadius: "50%",
            border: `2px solid ${BORDER_SOFT}`, borderTopColor: ORANGE,
            animation: "mwild-spin 0.8s linear infinite", display: "inline-block",
          }} />
        </div>
      ) : state.commentsError && comments.length === 0 ? (
        <div style={{ padding: "14px 0", fontSize: 13, color: INK_FAINT }}>
          Couldn't load comments.
        </div>
      ) : comments.length === 0 ? (
        <div style={{ padding: "14px 0", fontSize: 13, color: INK_FAINT }}>
          Be the first to comment.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {comments.map((c, i) => (
            <div key={c.id || i}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>
                  {c.authorName || "Anonymous"}
                </span>
                {c.authorByline && (
                  <span style={{ fontSize: 11, color: INK_FAINT }}>
                    {c.authorByline}
                  </span>
                )}
                <span style={{ fontSize: 11, color: INK_FAINT, marginLeft: "auto" }}>
                  {formatAgo(c.createdAt)}
                </span>
              </div>
              <p style={{ margin: "4px 0 0", fontSize: 13.5, color: INK, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                {c.body}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={submit}
        style={{
          marginTop: 18,
          display: "flex", alignItems: "flex-end", gap: 6,
          padding: "4px 4px 4px 14px",
          background: CARD, borderRadius: 20,
          border: `1px solid ${BORDER_SOFT}`,
        }}
      >
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit() }
          }}
          placeholder="Add a comment…"
          rows={1}
          style={{
            flex: 1, resize: "none", border: "none", outline: "none",
            background: "transparent", padding: "9px 0",
            fontSize: 13.5, lineHeight: 1.5, color: INK, fontFamily: FF,
            minHeight: 24, maxHeight: 140,
          }}
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          aria-label="Send"
          style={{
            width: 32, height: 32, borderRadius: 999,
            border: "none",
            background: text.trim() && !sending ? INK : SURFACE_ALT,
            color: text.trim() && !sending ? "#fff" : INK_FAINT,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            cursor: text.trim() && !sending ? "pointer" : "default",
            margin: 4, flexShrink: 0,
          }}
        >
          <Send size={14} />
        </button>
      </form>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
export default function MobileWildTab({
  userId, onTabChange, activeTab, pendingApplications,
}) {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activity, setActivity] = useState({})
  const [selectedId, setSelectedId] = useState(null)
  const voteInFlight = useRef(new Set())

  const applyPosts = (list) => {
    const normalized = list.map(classifyPost)
    setPosts(normalized)
    setActivity(prev => {
      const next = {}
      for (const p of normalized) {
        next[p.id] = prev[p.id] || {
          vote: 0, votes: p.votes, commentCount: p.commentCount,
          comments: null, loadingComments: false, commentsError: null,
        }
      }
      return next
    })
  }

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/wild/posts?limit=50`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const raw = await res.text()
      const body = JSON.parse(raw)
      applyPosts(body.posts || [])
      setError(null)
      try { localStorage.setItem(POSTS_CACHE_KEY, raw) } catch {}
    } catch (err) {
      console.error("Wild fetch failed:", err)
      if (posts.length === 0) setError(String(err))
    } finally { setLoading(false) }
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(POSTS_CACHE_KEY)
      if (raw) {
        const body = JSON.parse(raw)
        if ((body.posts || []).length > 0) {
          applyPosts(body.posts)
          setLoading(false)
        }
      }
    } catch {}
    load()
  }, [])

  const selectedPost = useMemo(
    () => (selectedId ? posts.find(p => p.id === selectedId) : null),
    [posts, selectedId]
  )

  const setPostState = (postId, updater) => {
    setActivity(prev => {
      const cur = prev[postId] || {
        vote: 0, votes: 0, commentCount: 0,
        comments: null, loadingComments: false, commentsError: null,
      }
      const next = typeof updater === "function" ? updater(cur) : updater
      return { ...prev, [postId]: next }
    })
  }

  const vote = async (post, value) => {
    if (!userId) { toast.error("Sign in to vote"); return }
    if (voteInFlight.current.has(post.id)) return
    const cur = activity[post.id]
    if (!cur) return
    const target = cur.vote === value ? 0 : value
    const prevVote = cur.vote
    const prevVotes = cur.votes
    const optimistic = prevVotes - prevVote + target
    setPostState(post.id, s => ({ ...s, vote: target, votes: optimistic }))
    voteInFlight.current.add(post.id)
    try {
      const res = await fetch(`${API_BASE}/wild/posts/${post.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, value: target }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const updated = Number(data.votes)
      if (Number.isFinite(updated)) setPostState(post.id, s => ({ ...s, votes: updated }))
    } catch (err) {
      console.error("Vote failed:", err)
      setPostState(post.id, s => ({ ...s, vote: prevVote, votes: prevVotes }))
      toast.error("Couldn't register vote")
    } finally {
      voteInFlight.current.delete(post.id)
    }
  }

  const messageBoss = (post) => {
    if (post.externalUrl) {
      window.open(post.externalUrl, "_blank", "noopener,noreferrer")
      return
    }
    toast("Messaging coming soon")
  }

  const headerPad = "calc(14px + env(safe-area-inset-top))"

  // ── Detail view ─────────────────────────────────────────────────────────
  if (selectedPost) {
    const state = activity[selectedPost.id] || {}
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 150,
        background: BG, color: INK, fontFamily: FF,
        display: "flex", flexDirection: "column",
      }}>
        <style>{`
          @keyframes mwild-spin { to { transform: rotate(360deg); } }
          @keyframes mwild-shim { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
        `}</style>

        {/* Header */}
        <div style={{
          padding: `${headerPad} 12px 10px`,
          display: "flex", alignItems: "center", gap: 8,
          background: BG, borderBottom: `1px solid ${BORDER_SOFT}`,
        }}>
          <button
            onClick={() => setSelectedId(null)}
            aria-label="Back"
            style={{
              width: 36, height: 36, border: "none", background: "transparent",
              color: INK, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 15, fontWeight: 800, letterSpacing: 0.2,
          }}>
            JOBS IN THE <span style={{ color: ORANGE }}>WILD</span>
          </div>
          <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 600, letterSpacing: 1, color: INK_FAINT }}>
            BETA
          </span>
        </div>

        <div style={{
          flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
          padding: "14px 14px 108px",
        }}>
          <HiringCard
            post={selectedPost}
            state={state}
            expanded
            onVote={vote}
            onMessageBoss={messageBoss}
          />

          {selectedPost.externalUrl && (
            <a
              href={selectedPost.externalUrl}
              target="_blank" rel="noopener noreferrer"
              style={{
                marginTop: 14,
                display: "flex", alignItems: "center", gap: 8,
                padding: "12px 14px",
                background: CARD, border: `1px solid ${BORDER_SOFT}`,
                borderRadius: 14,
                textDecoration: "none", color: INK, fontFamily: FF,
              }}
            >
              <ArrowUpRight size={16} color={INK_MUTED} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                View original post
              </span>
              <span style={{ fontSize: 11, color: INK_FAINT }}>Open ↗</span>
            </a>
          )}

          <CommentsSection
            postId={selectedPost.id}
            userId={userId}
            state={state}
            setState={(updater) => setPostState(selectedPost.id, updater)}
          />
        </div>

        <MobileBottomNav
          activeTab={activeTab}
          onTabChange={onTabChange}
          pendingApplications={pendingApplications || 0}
        />
      </div>
    )
  }

  // ── Feed view ────────────────────────────────────────────────────────────
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 150,
      background: BG, color: INK, fontFamily: FF,
      display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @keyframes mwild-spin { to { transform: rotate(360deg); } }
        @keyframes mwild-shim { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      `}</style>

      <div style={{
        flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
        paddingTop: headerPad, paddingBottom: 108,
      }}>
        {/* Header */}
        <div style={{
          padding: "0 18px 14px",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: ORANGE, color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 800, letterSpacing: -0.5,
          }}>W</div>
          <div style={{
            flex: 1, minWidth: 0,
            fontSize: 20, fontWeight: 800, color: INK, letterSpacing: -0.2, lineHeight: 1.2,
          }}>
            JOBS IN THE <span style={{ color: ORANGE }}>WILD</span>
          </div>
          <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 1, color: INK_FAINT }}>
            BETA
          </span>
        </div>

        {/* Feed body */}
        {loading && posts.length === 0 ? (
          <div style={{ padding: "0 14px", display: "flex", flexDirection: "column", gap: 16 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ animation: "mwild-shim 1.4s ease-in-out infinite" }}>
                <CardSkeleton />
              </div>
            ))}
          </div>
        ) : error && posts.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "48px 32px", textAlign: "center", gap: 10,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>
              Couldn't load posts
            </div>
            <p style={{ margin: 0, fontSize: 13, color: INK_FAINT, lineHeight: 1.5 }}>
              Check your connection and try again.
            </p>
            <button
              onClick={() => { setLoading(true); load() }}
              style={{
                marginTop: 6,
                padding: "9px 20px", borderRadius: 999, border: "none",
                background: ORANGE, color: "#fff", cursor: "pointer",
                fontFamily: FF, fontSize: 13, fontWeight: 700,
              }}
            >
              Retry
            </button>
          </div>
        ) : posts.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "48px 32px", textAlign: "center", gap: 8,
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>
              Nothing in the wild yet
            </div>
            <p style={{ margin: 0, fontSize: 13, color: INK_FAINT, lineHeight: 1.5 }}>
              Be the first to post a hiring shout here.
            </p>
          </div>
        ) : (
          <div style={{ padding: "0 14px", display: "flex", flexDirection: "column", gap: 16 }}>
            {posts.map(p => (
              <HiringCard
                key={p.id}
                post={p}
                state={activity[p.id]}
                onOpen={() => setSelectedId(p.id)}
                onVote={vote}
                onMessageBoss={messageBoss}
              />
            ))}
          </div>
        )}
      </div>

      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        pendingApplications={pendingApplications || 0}
      />
    </div>
  )
}
